import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  ViewChild,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { fromEvent, merge } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TourService } from '../../../core/services/tour.service';
import { TourPopoverPosition } from '../../../core/models/types/tour-popover-position.type';

@Component({
  selector: 'app-tour-popover',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './tour-popover.component.html',
  styleUrl: './tour-popover.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TourPopoverComponent {
  private readonly POPOVER_WIDTH = 320;
  private readonly VIEWPORT_MARGIN = 12;
  private readonly ARROW_SIZE = 12;
  private readonly HIGHLIGHT_PADDING = 8;
  private readonly STEP_GAP = 12;
  private readonly POPOVER_HEIGHT_ESTIMATE = 160;

  private readonly tourService = inject(TourService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('popoverEl', { read: ElementRef<HTMLElement> })
  readonly popoverRef!: ElementRef<HTMLElement>;

  readonly arrowStyle = signal<Record<string, string>>({});
  readonly highlightStyle = signal<Record<string, string>>({});
  readonly popoverStyle = signal<Record<string, string>>({});
  readonly popoverPosition = signal<TourPopoverPosition>('bottom');

  readonly isActive = this.tourService.isActive;
  readonly currentStep = this.tourService.currentStep;
  readonly totalSteps = this.tourService.totalSteps;
  readonly currentIndex = this.tourService.currentIndex;

  constructor() {
    effect(() => {
      const step = this.currentStep();

      if (step && this.isActive()) {
        this.scrollTargetIntoView(step.elementRef.nativeElement);

        this.recalculate();
      }
    });

    merge(
      fromEvent(window, 'scroll', { capture: true }),
      fromEvent(window, 'resize'),
    )
      .pipe(debounceTime(16), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isActive() && this.currentStep()) this.recalculate();
      });
  }

  private scrollTargetIntoView(target: HTMLElement): void {
    const container = this.findScrollContainer(target);

    if (!container) return;

    const targetRect = target.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const targetCenter =
      targetRect.top -
      containerRect.top +
      container.scrollTop +
      targetRect.height / 2;

    const desiredScrollTop = targetCenter - container.clientHeight / 2;
    const maxScroll = container.scrollHeight - container.clientHeight;

    container.scrollTop = Math.max(0, Math.min(desiredScrollTop, maxScroll));
  }

  private findScrollContainer(el: HTMLElement): HTMLElement | null {
    let node: HTMLElement | null = el.parentElement;

    while (node && node !== document.documentElement) {
      const overflowY = getComputedStyle(node).overflowY;

      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        node.scrollHeight > node.clientHeight
      ) {
        return node;
      }

      node = node.parentElement;
    }

    return null;
  }

  private recalculate(): void {
    const step = this.currentStep();

    if (!step) return;

    const targetEl = step.elementRef.nativeElement;
    const targetRect = targetEl.getBoundingClientRect();
    const popoverWidth = Math.min(
      this.POPOVER_WIDTH,
      window.innerWidth - this.VIEWPORT_MARGIN * 2,
    );

    this.highlightStyle.set({
      top: `${targetRect.top - this.HIGHLIGHT_PADDING}px`,
      left: `${targetRect.left - this.HIGHLIGHT_PADDING}px`,
      width: `${targetRect.width + this.HIGHLIGHT_PADDING * 2}px`,
      height: `${targetRect.height + this.HIGHLIGHT_PADDING * 2}px`,
    });

    const resolved = this.resolvePosition(
      targetRect,
      popoverWidth,
      this.POPOVER_HEIGHT_ESTIMATE,
      step.popoverPosition,
    );

    this.popoverPosition.set(resolved.position);

    this.popoverStyle.set({
      top: `${resolved.top}px`,
      left: `${resolved.left}px`,
      transform: 'none',
    });

    this.setArrow(
      resolved.position,
      targetRect,
      resolved.top,
      resolved.left,
      popoverWidth,
    );

    requestAnimationFrame(() => this.applyMeasuredPosition(targetRect));
  }

  private applyMeasuredPosition(targetRect: DOMRect): void {
    if (!this.popoverRef) return;

    const step = this.currentStep();

    if (!step) return;

    const el = this.popoverRef.nativeElement;
    const measuredWidth = el.offsetWidth;
    const measuredHeight = el.offsetHeight;

    const resolved = this.resolvePosition(
      targetRect,
      measuredWidth,
      measuredHeight,
      step.popoverPosition,
    );

    this.popoverPosition.set(resolved.position);

    this.popoverStyle.set({
      top: `${resolved.top}px`,
      left: `${resolved.left}px`,
      transform: 'none',
    });

    this.setArrow(
      resolved.position,
      targetRect,
      resolved.top,
      resolved.left,
      measuredWidth,
    );
  }

  private resolvePosition(
    targetRect: DOMRect,
    width: number,
    height: number,
    preferred: TourPopoverPosition,
  ): { top: number; left: number; position: TourPopoverPosition } {
    const reservedTop = this.getReservedTop();
    const maxTop = window.innerHeight - height - this.VIEWPORT_MARGIN;

    for (const position of this.getCandidatePositions(preferred)) {
      const raw = this.computeRawPosition(targetRect, width, height, position);

      if (raw.top >= reservedTop && raw.top <= maxTop) {
        return {
          top: raw.top,
          left: this.clampHorizontally(raw.left, width),
          position,
        };
      }
    }

    const fallback = this.computeRawPosition(
      targetRect,
      width,
      height,
      preferred,
    );

    return {
      top: Math.max(reservedTop, Math.min(fallback.top, maxTop)),
      left: this.clampHorizontally(fallback.left, width),
      position: preferred,
    };
  }

  private getCandidatePositions(
    preferred: TourPopoverPosition,
  ): TourPopoverPosition[] {
    const flip: Record<TourPopoverPosition, TourPopoverPosition> = {
      top: 'bottom',
      bottom: 'top',
      left: 'right',
      right: 'left',
    };

    return [preferred, flip[preferred]];
  }

  private computeRawPosition(
    targetRect: DOMRect,
    width: number,
    height: number,
    position: TourPopoverPosition,
  ): { top: number; left: number } {
    const targetCenterX = targetRect.left + targetRect.width / 2;
    const targetCenterY = targetRect.top + targetRect.height / 2;

    let top: number;
    let left: number;

    switch (position) {
      case 'bottom':
        top = targetRect.bottom + this.STEP_GAP;
        left = targetCenterX - width / 2;
        break;
      case 'top':
        top = targetRect.top - this.STEP_GAP - height;
        left = targetCenterX - width / 2;
        break;
      case 'left':
        top = targetCenterY - height / 2;
        left = targetRect.left - this.STEP_GAP - width;
        break;
      case 'right':
        top = targetCenterY - height / 2;
        left = targetRect.right + this.STEP_GAP;
        break;
    }

    return { top, left };
  }

  private clampHorizontally(left: number, width: number): number {
    const viewportWidth = window.innerWidth;
    const margin = this.VIEWPORT_MARGIN;

    return Math.max(margin, Math.min(left, viewportWidth - width - margin));
  }

  private getReservedTop(): number {
    const navbar = document.querySelector<HTMLElement>('app-navbar');

    if (!navbar) return this.VIEWPORT_MARGIN;

    return navbar.getBoundingClientRect().bottom + this.VIEWPORT_MARGIN;
  }

  private setArrow(
    popoverPosition: TourPopoverPosition,
    targetRect: DOMRect,
    popoverTop: number,
    popoverLeft: number,
    popoverWidth: number,
  ): void {
    const halfArrow = this.ARROW_SIZE / 2;
    const minOffset = halfArrow + 4;
    const style: Record<string, string> = {};

    if (popoverPosition === 'bottom' || popoverPosition === 'top') {
      style[popoverPosition === 'bottom' ? 'top' : 'bottom'] =
        `${-halfArrow}px`;

      const targetCenterX =
        targetRect.left + targetRect.width / 2 - popoverLeft;

      style['left'] = `${Math.max(
        minOffset,
        Math.min(targetCenterX - halfArrow, popoverWidth - halfArrow * 2 - 4),
      )}px`;
    } else {
      style[popoverPosition === 'left' ? 'right' : 'left'] = `${-halfArrow}px`;

      const targetCenterY = targetRect.top + targetRect.height / 2 - popoverTop;

      style['top'] = `${Math.max(minOffset, targetCenterY - halfArrow)}px`;
    }

    this.arrowStyle.set(style);
  }

  showNextStep(): void {
    this.tourService.showNextStep();
  }

  showPreviousStep(): void {
    this.tourService.showPreviousStep();
  }

  endTour(): void {
    this.tourService.endTour();
  }
}
